import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { syncBlacklistToIndexedDB } from './blacklistService';
import { matchesSearchTerm } from './searchUtils';
import { isCacheStale, recordDataSync } from './cacheService';
import { conditionalLog } from './consoleLogger';

/**
 * Local-first Technician Service for STB Workshop
 * Prioritizes IndexedDB cache with Firestore fallback to minimize reads
 */

// ===== TECHNICIAN DATA OPERATIONS =====

/**
 * Gets all technicians with local-first architecture
 * Uses IndexedDB cache first, falls back to Firestore only when needed
 * @param {boolean} includeArchived - Whether to include archived technicians
 * @param {boolean} forceRefresh - Force refresh from Firestore
 * @returns {Promise<Array>} Array of technician objects
 */
export const getAllTechnicians = async (includeArchived = false, forceRefresh = false) => {
  try {
    // If not forcing refresh, try IndexedDB first but check cache freshness
    if (!forceRefresh) {
      const cachedTechnicians = await getTechniciansFromCache();
      
      if (cachedTechnicians && cachedTechnicians.length > 0) {
        // Check if cache is stale
        if (!isCacheStale('technicians')) {
          conditionalLog(`Using cached technicians data (${cachedTechnicians.length} technicians)`);
          
          // Deserialize dates from cached data
          const deserializedTechnicians = cachedTechnicians.map(tech => deserializeDates(tech));
          
          // Filter archived technicians locally if needed
          let filteredTechnicians = deserializedTechnicians;
          if (!includeArchived) {
            filteredTechnicians = deserializedTechnicians.filter(tech => !tech.isArchived);
          }
          
          // Sort manually by fechaIngreso (newest first)
          filteredTechnicians.sort((a, b) => {
            const dateA = a.fechaIngreso instanceof Date ? a.fechaIngreso : new Date(a.fechaIngreso || 0);
            const dateB = b.fechaIngreso instanceof Date ? b.fechaIngreso : new Date(b.fechaIngreso || 0);
            return dateB - dateA;
          });
          
          return filteredTechnicians;
        } else {
          conditionalLog('Cached technicians are stale, fetching fresh data from Firestore...');
        }
      } else {
        conditionalLog('No cached technicians found, fetching from Firestore...');
      }
    } else {
      conditionalLog('Force refresh requested, fetching from Firestore...');
    }
    
    // Cache miss or force refresh - fetch from Firestore
    return await fetchAndCacheTechnicians(includeArchived);
    
  } catch (error) {
    console.error('Error getting technicians:', error);
    
    // Fallback to cached data if Firestore fails
    try {
      const cachedTechnicians = await getTechniciansFromCache();
      if (cachedTechnicians && cachedTechnicians.length > 0) {
        conditionalLog('Using cached technicians as fallback');
        return cachedTechnicians.map(tech => deserializeDates(tech));
      }
    } catch (cacheError) {
      console.error('Cache fallback also failed:', cacheError);
    }
    
    return [];
  }
};

/**
 * Fetches technicians from Firestore and updates IndexedDB cache
 * @param {boolean} includeArchived - Whether to include archived technicians  
 * @returns {Promise<Array>} Array of technician objects
 */
const fetchAndCacheTechnicians = async (includeArchived = false) => {
  try {
    // Always fetch ALL technicians from Firestore to ensure complete cache
    const q = query(collection(db, 'technicians'));
    
    const querySnapshot = await getDocs(q);
    const allTechniciansList = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort manually by fechaIngreso (newest first)
    allTechniciansList.sort((a, b) => {
      const dateA = a.fechaIngreso?.toDate ? a.fechaIngreso.toDate() : new Date(a.fechaIngreso || 0);
      const dateB = b.fechaIngreso?.toDate ? b.fechaIngreso.toDate() : new Date(b.fechaIngreso || 0);
      return dateB - dateA;
    });
    
    // Update IndexedDB cache with ALL technicians data
    await syncTechniciansToCache(allTechniciansList);
    
    // Record cache sync time
    recordDataSync('technicians');
    
    // Also sync blacklist incidents for offline access
    await syncBlacklistIncidents();
    
    // Filter the result based on includeArchived parameter
    let filteredTechnicians = allTechniciansList;
    if (!includeArchived) {
      filteredTechnicians = allTechniciansList.filter(tech => !tech.isArchived);
    }
    
    conditionalLog(`Fetched ${allTechniciansList.length} technicians from Firestore, updated cache, returning ${filteredTechnicians.length}`);
    return filteredTechnicians;
    
  } catch (error) {
    console.error('Error fetching technicians from Firestore:', error);
    throw error;
  }
};

/**
 * Gets technicians that are in use in orders (optimized with cached data)
 * @param {Array} technicianIds - Array of technician IDs to check
 * @returns {Promise<Set>} Set of technician IDs that are in use
 */
export const getTechniciansInUse = async (technicianIds) => {
  try {
    const inUseSet = new Set();
    
    // Try to get orders from IndexedDB cache first
    const cachedOrders = await getOrdersFromCache();
    
    if (cachedOrders && cachedOrders.length > 0) {
      conditionalLog('Using cached orders data to check technician usage');
      
      // Check which technicians are used in cached orders
      cachedOrders.forEach(order => {
        if (order.tecnicos && Array.isArray(order.tecnicos)) {
          order.tecnicos.forEach(techId => {
            if (technicianIds.includes(techId)) {
              inUseSet.add(techId);
            }
          });
        }
      });
      
      return inUseSet;
    }
    
    // Fallback: if no cached orders, do a single query for all orders
    conditionalLog('No cached orders, querying Firestore for technician usage');
    const ordersQuery = query(collection(db, 'orders'));
    const ordersSnapshot = await getDocs(ordersQuery);
    
    ordersSnapshot.docs.forEach(doc => {
      const order = doc.data();
      if (order.tecnicos && Array.isArray(order.tecnicos)) {
        order.tecnicos.forEach(techId => {
          if (technicianIds.includes(techId)) {
            inUseSet.add(techId);
          }
        });
      }
    });
    
    return inUseSet;
  } catch (error) {
    console.error('Error checking technicians usage:', error);
    return new Set(); // Return empty set on error
  }
};

/**
 * Checks if a specific technician is used in any orders (optimized)
 * @param {string} technicianId - Technician ID to check
 * @returns {Promise<boolean>} Whether technician is in use
 */
export const isTechnicianInUse = async (technicianId) => {
  const inUseSet = await getTechniciansInUse([technicianId]);
  return inUseSet.has(technicianId);
};

/**
 * Searches technicians locally using cached data
 * @param {string} searchTerm - Search term
 * @param {boolean} includeArchived - Whether to include archived technicians
 * @returns {Promise<Array>} Filtered technicians
 */
export const searchTechniciansLocal = async (searchTerm, includeArchived = false) => {
  try {
    if (!searchTerm.trim()) {
      return await getAllTechnicians(includeArchived);
    }
    
    const technicians = await getAllTechnicians(includeArchived);
    
    return technicians.filter(tech => 
      matchesSearchTerm(tech.nombre, searchTerm) ||
      matchesSearchTerm(tech.telefono, searchTerm) ||
      matchesSearchTerm(tech.domicilio, searchTerm)
    );
  } catch (error) {
    console.error('Error searching technicians locally:', error);
    return [];
  }
};

// ===== INDEXEDDB CACHE FUNCTIONS =====

/**
 * Gets technicians from IndexedDB cache
 * @returns {Promise<Array>} Array of cached technicians
 */
const getTechniciansFromCache = async () => {
  try {
    const request = indexedDB.open('STBWorkshop', 23);
    
    return new Promise((resolve, reject) => {
      request.onerror = () => resolve(null);
      
      request.onsuccess = () => {
        const db = request.result;
        
        if (!db.objectStoreNames.contains('technicians')) {
          resolve(null);
          return;
        }
        
        const transaction = db.transaction(['technicians'], 'readonly');
        const store = transaction.objectStore('technicians');
        const getAllRequest = store.getAll();
        
        getAllRequest.onsuccess = () => {
          const technicians = getAllRequest.result || [];
          resolve(technicians);
        };
        
        getAllRequest.onerror = () => resolve(null);
      };
    });
  } catch (error) {
    console.error('Error getting technicians from cache:', error);
    return null;
  }
};

/**
 * Gets orders from IndexedDB cache for usage checking
 * @returns {Promise<Array>} Array of cached orders
 */
const getOrdersFromCache = async () => {
  try {
    const request = indexedDB.open('STBWorkshop', 23);
    
    return new Promise((resolve, reject) => {
      request.onerror = () => resolve([]);
      
      request.onsuccess = () => {
        const db = request.result;
        
        if (!db.objectStoreNames.contains('orders')) {
          resolve([]);
          return;
        }
        
        const transaction = db.transaction(['orders'], 'readonly');
        const store = transaction.objectStore('orders');
        const getAllRequest = store.getAll();
        
        getAllRequest.onsuccess = () => {
          const orders = getAllRequest.result || [];
          resolve(orders);
        };
        
        getAllRequest.onerror = () => resolve([]);
      };
    });
  } catch (error) {
    console.error('Error getting orders from cache:', error);
    return [];
  }
};

/**
 * Syncs technicians to IndexedDB cache
 * @param {Array} technicians - Technicians to sync
 * @returns {Promise<boolean>} Success status
 */
const syncTechniciansToCache = async (technicians) => {
  try {
    const request = indexedDB.open('STBWorkshop', 23);
    
    return new Promise((resolve, reject) => {
      request.onerror = () => {
        console.error('IndexedDB open error:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        const db = request.result;
        
        if (!db.objectStoreNames.contains('technicians')) {
          // Store doesn't exist, resolve without error
          resolve(true);
          return;
        }
        
        const transaction = db.transaction(['technicians'], 'readwrite');
        const store = transaction.objectStore('technicians');
        
        // Clear existing data
        store.clear();
        
        // Add all technicians
        technicians.forEach(technician => {
          store.put(technician);
        });
        
        transaction.oncomplete = () => {
          conditionalLog(`Synced ${technicians.length} technicians to IndexedDB`);
          resolve(true);
        };
        
        transaction.onerror = () => {
          console.error('IndexedDB transaction error:', transaction.error);
          reject(transaction.error);
        };
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('technicians')) {
          db.createObjectStore('technicians', { keyPath: 'id' });
        }
      };
    });
  } catch (error) {
    console.error('Error syncing technicians to IndexedDB:', error);
    return false;
  }
};

/**
 * Syncs blacklist incidents to IndexedDB for offline access
 * @returns {Promise<void>}
 */
const syncBlacklistIncidents = async () => {
  try {
    // Fetch all blacklist incidents from Firestore
    const blacklistQuery = query(collection(db, 'blacklist_incidents'));
    const blacklistSnapshot = await getDocs(blacklistQuery);
    
    const incidents = blacklistSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sync to IndexedDB
    await syncBlacklistToIndexedDB(incidents);
    conditionalLog(`Synced ${incidents.length} blacklist incidents to cache`);
  } catch (error) {
    console.error('Error syncing blacklist incidents:', error);
    // Don't throw - this is optional for performance
  }
};

// ===== UTILITY FUNCTIONS =====

/**
 * Converts serialized date objects back to Date objects
 * Handles Firestore Timestamps that have been serialized to IndexedDB
 * @param {Object} obj - Object that may contain serialized dates
 * @returns {Object} Object with dates converted to Date objects
 */
const deserializeDates = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const result = Array.isArray(obj) ? [] : {};
  
  for (const key in obj) {
    const value = obj[key];
    
    if (value && typeof value === 'object') {
      // Check if this looks like a serialized Firestore Timestamp
      if (value.seconds && typeof value.seconds === 'number') {
        result[key] = new Date(value.seconds * 1000);
      } else if (Array.isArray(value)) {
        result[key] = value.map(item => deserializeDates(item));
      } else {
        result[key] = deserializeDates(value);
      }
    } else {
      result[key] = value;
    }
  }
  
  return result;
};